import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowUp, ArrowDown, Pencil, Trash2, Plus, Check, X, Archive, ListChecks } from 'lucide-react';
import { adminEnquetesAPI } from '../../utils/adminEnquetesAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';
import PerguntaForm from './PerguntaForm';
import OpcaoListEditor from './OpcaoListEditor';
import UploadImagemPergunta from './UploadImagemPergunta';

const TIPO_BADGE = {
  unica_escolha: 'bg-blue-50 text-blue-700',
  multipla_escolha: 'bg-purple-50 text-purple-700',
  aberta: 'bg-amber-50 text-amber-700'
};

const TIPO_LABEL = {
  unica_escolha: 'Única escolha',
  multipla_escolha: 'Múltipla escolha',
  aberta: 'Aberta'
};

// Uma linha de pergunta ativa: exibição + edição inline de texto/obrigatória
// + controles de ordem + opções + imagem.
const PerguntaItem = ({ enqueteId, pergunta, isPrimeira, isUltima, onChange }) => {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(pergunta.texto);
  const [obrigatoria, setObrigatoria] = useState(!!pergunta.obrigatoria);

  const salvar = async () => {
    if (!texto.trim()) return;
    try {
      await adminEnquetesAPI.atualizarPergunta(enqueteId, pergunta.id, { texto: texto.trim(), obrigatoria });
      setEditando(false);
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao editar pergunta');
    }
  };

  const cancelarEdicao = () => {
    setTexto(pergunta.texto);
    setObrigatoria(!!pergunta.obrigatoria);
    setEditando(false);
  };

  const mover = async (direcao) => {
    try {
      await adminEnquetesAPI.moverPergunta(enqueteId, pergunta.id, direcao);
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Não foi possível mover');
    }
  };

  const remover = async () => {
    if (!window.confirm(`Remover a pergunta "${pergunta.texto}" do formulário público?`)) return;
    try {
      await adminEnquetesAPI.removerPergunta(enqueteId, pergunta.id);
      sucessoComHorario('Pergunta removida do formulário');
      onChange();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao remover pergunta');
    }
  };

  return (
    <div className="group rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 p-4 sm:p-5 bg-white">
      <div className="flex items-start gap-2 sm:gap-3">
        {/* Ordem — linha horizontal no mobile (economiza largura), coluna no desktop */}
        <div className="flex sm:flex-col gap-1 flex-shrink-0 pt-0.5">
          <button
            onClick={() => mover('subir')}
            disabled={isPrimeira}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-20 disabled:hover:bg-transparent transition-colors"
            aria-label="Mover pra cima"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            onClick={() => mover('descer')}
            disabled={isUltima}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-20 disabled:hover:bg-transparent transition-colors"
            aria-label="Mover pra baixo"
          >
            <ArrowDown className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          {editando ? (
            <div className="space-y-2.5">
              <input
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all"
                autoFocus
              />
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={obrigatoria} onChange={(e) => setObrigatoria(e.target.checked)} className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500" />
                Obrigatória
              </label>
              <div className="flex items-center gap-2">
                <button onClick={salvar} className="flex items-center gap-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-1.5 transition-colors">
                  <Check className="w-3.5 h-3.5" /> Salvar
                </button>
                <button onClick={cancelarEdicao} className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg px-3 py-1.5 transition-colors">
                  <X className="w-3.5 h-3.5" /> Cancelar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <p className="font-medium text-gray-900 leading-snug">{pergunta.texto}</p>

                <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => setEditando(true)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={remover} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TIPO_BADGE[pergunta.tipo]}`}>
                  {TIPO_LABEL[pergunta.tipo]}
                </span>
                {pergunta.obrigatoria ? (
                  <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">obrigatória</span>
                ) : (
                  <span className="text-xs font-medium bg-gray-50 text-gray-400 px-2 py-0.5 rounded-full">opcional</span>
                )}
              </div>

              {pergunta.tipo !== 'aberta' && (
                <OpcaoListEditor enqueteId={enqueteId} pergunta={pergunta} opcoes={pergunta.opcoes} onChange={onChange} />
              )}

              <UploadImagemPergunta enqueteId={enqueteId} pergunta={pergunta} onChange={onChange} />

              {/* No mobile as ações ficam abaixo (sem hover pra descobrir) */}
              <div className="flex sm:hidden items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <button onClick={() => setEditando(true)} className="flex items-center gap-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg px-2.5 py-1.5 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
                <button onClick={remover} className="flex items-center gap-1.5 text-sm text-red-500 hover:bg-red-50 rounded-lg px-2.5 py-1.5 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Remover
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Bloco "Perguntas" — autocontido (busca seus próprios dados), usado tanto
// dentro do modal "Editar Perguntas" (a partir de Gerenciar Enquetes)
// quanto na página cheia de criação (EditorEnquete.js).
const PerguntasBuilder = ({ enqueteId }) => {
  const [enquete, setEnquete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostrarFormPergunta, setMostrarFormPergunta] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const dados = await adminEnquetesAPI.obter(enqueteId);
      setEnquete(dados);
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao carregar enquete');
    } finally {
      setLoading(false);
    }
  }, [enqueteId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const onCriarPergunta = async (dadosPergunta) => {
    try {
      await adminEnquetesAPI.criarPergunta(enqueteId, dadosPergunta);
      sucessoComHorario('Pergunta criada');
      setMostrarFormPergunta(false);
      carregar();
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao criar pergunta');
      throw error;
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="loading-spinner" /></div>;
  }

  if (!enquete) {
    return <p className="text-gray-500 text-center py-8">Enquete não encontrada.</p>;
  }

  const perguntasAtivas = (enquete.perguntas || []).filter((p) => p.ativa).sort((a, b) => a.ordem - b.ordem);
  const perguntasInativas = (enquete.perguntas || []).filter((p) => !p.ativa);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary-600" /> Perguntas
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">{perguntasAtivas.length} de 30 perguntas</p>
        </div>
        <button
          onClick={() => setMostrarFormPergunta((v) => !v)}
          className="btn-primary flex items-center gap-2 text-sm px-3.5 py-2.5 rounded-xl shadow-sm flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden xs:inline">Nova pergunta</span>
          <span className="xs:hidden">Nova</span>
        </button>
      </div>

      {mostrarFormPergunta && (
        <div className="mb-4">
          <PerguntaForm onCriar={onCriarPergunta} onCancelar={() => setMostrarFormPergunta(false)} />
        </div>
      )}

      <div className="space-y-3">
        {perguntasAtivas.map((pergunta, index) => (
          <PerguntaItem
            key={pergunta.id}
            enqueteId={enqueteId}
            pergunta={pergunta}
            isPrimeira={index === 0}
            isUltima={index === perguntasAtivas.length - 1}
            onChange={carregar}
          />
        ))}
        {perguntasAtivas.length === 0 && !mostrarFormPergunta && (
          <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
            <ListChecks className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Nenhuma pergunta ainda.</p>
            <p className="text-gray-400 text-xs mt-0.5">Clique em "Nova pergunta" pra começar.</p>
          </div>
        )}
      </div>

      {perguntasInativas.length > 0 && (
        <details className="mt-4 group">
          <summary className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1.5 select-none">
            <Archive className="w-3.5 h-3.5" />
            {perguntasInativas.length} pergunta(s) removida(s) (histórico, fora do formulário público)
          </summary>
          <ul className="mt-2 space-y-1 pl-5">
            {perguntasInativas.map((p) => (
              <li key={p.id} className="text-xs text-gray-400 italic">{p.texto}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default PerguntasBuilder;
